import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChatCircleText,
  PaperPlaneTilt,
  Sparkle,
  X,
  Check,
  WarningCircle,
  ArrowUpRight,
  Paperclip,
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from './ui/button';
import { AIPremiumDialog } from './AIPremiumGate';
import { cn } from '../lib/utils';

const SUGGESTIONS = [
  'What is my itinerary?',
  'When is the reception?',
  'Update my ticket details',
];

const linkHref = (path, params) => {
  if (!path) return null;
  if (!params) return path;
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) search.set(key, value);
  });
  const query = search.toString();
  return query ? `${path}?${query}` : path;
};

const canonicalizeUrl = (value) => value.replace(/^https?:\/\/app\.galago\.in(?=\/|$)/i, 'https://galago.in');

const renderMessageContent = (content) => {
  const text = String(content || '');
  const parts = text.split(/(https?:\/\/[^\s<]+)/gi);
  return parts.map((part, index) => {
    if (!/^https?:\/\//i.test(part)) return <React.Fragment key={index}>{part}</React.Fragment>;
    const href = canonicalizeUrl(part.replace(/[),.;!?]+$/, ''));
    const trailing = part.slice(part.replace(/[),.;!?]+$/, '').length);
    return (
      <React.Fragment key={index}>
        <a href={href} target="_blank" rel="noreferrer" className="font-medium text-wedding-sage underline underline-offset-2 hover:text-wedding-sage/80">
          {href}
        </a>
        {trailing ? part.slice(-trailing) : ''}
      </React.Fragment>
    );
  });
};

const ChatBubble = ({ role, content, citations }) => {
  const navigate = useNavigate();
  const go = (event, path, params) => {
    event.stopPropagation();
    const href = linkHref(path, params);
    if (href) navigate(href);
  };
  const isClickable = (item) => Boolean(item && item.path);
  return (
    <div className={cn('flex w-full', role === 'user' ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm',
          role === 'user'
            ? 'rounded-br-md bg-wedding-sage text-white'
            : 'rounded-bl-md border bg-card text-card-foreground'
        )}
      >
        {renderMessageContent(content)}
        {role !== 'user' && citations && citations.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {citations.map((citation, index) => {
              const clickable = isClickable(citation);
              return (
                <button
                  key={`${citation.kind}-${citation.id || index}`}
                  type="button"
                  disabled={!clickable}
                  onClick={(event) => clickable && go(event, citation.path, citation.params)}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border border-wedding-sage/30 bg-wedding-sage/10 px-2 py-0.5 text-[0.6875rem] font-medium text-wedding-sage',
                    clickable ? 'cursor-pointer transition hover:border-wedding-sage/60 hover:bg-wedding-sage/20' : 'cursor-default'
                  )}
                  title={clickable ? `Open ${citation.label}` : undefined}
                  data-testid={clickable ? 'chat-citation-link' : 'chat-citation'}
                >
                  <Sparkle className="h-3 w-3" weight="fill" />
                  {citation.label}
                  {clickable && <ArrowUpRight className="h-3 w-3" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default function ChatWidget() {
  const { currentEvent } = useAuth();
  const eventId = currentEvent?.id;

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [deepLinks, setDeepLinks] = useState([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [followUp, setFollowUp] = useState(null);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [error, setError] = useState('');
  const [ticketFile, setTicketFile] = useState(null);
  const [coachmarkVisible, setCoachmarkVisible] = useState(false);
  const [installPromptVisible, setInstallPromptVisible] = useState(
    () => typeof document !== 'undefined' && document.body?.dataset.galagoInstallPrompt === 'visible'
  );
  const [panelSize, setPanelSize] = useState(null);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const messageRevisionRef = useRef(0);
  const historyRequestRef = useRef(0);
  const messagesStorageKey = eventId ? `galago-chat-history-${eventId}` : null;
  const openStorageKey = eventId ? `galago-chat-open-${eventId}` : null;
  const coachmarkStorageKey = eventId ? `galago-chat-coachmark-dismissed-${eventId}` : null;

  useEffect(() => {
    if (!openStorageKey) {
      setOpen(false);
      return;
    }
    try {
      setOpen(sessionStorage.getItem(openStorageKey) === 'true');
    } catch (_err) { /* storage is optional */ }
  }, [openStorageKey]);

  const setChatOpen = useCallback((nextValue) => {
    setOpen((previous) => {
      const next = typeof nextValue === 'function' ? nextValue(previous) : nextValue;
      if (openStorageKey) {
        try { sessionStorage.setItem(openStorageKey, String(Boolean(next))); } catch (_err) { /* storage is optional */ }
      }
      return next;
    });
  }, [openStorageKey]);

  const dismissCoachmark = useCallback(() => {
    setCoachmarkVisible(false);
    if (coachmarkStorageKey) {
      try { sessionStorage.setItem(coachmarkStorageKey, 'true'); } catch (_err) { /* storage is optional */ }
    }
  }, [coachmarkStorageKey]);

  useEffect(() => {
    if (!eventId || open || !coachmarkStorageKey) {
      setCoachmarkVisible(false);
      return undefined;
    }
    try {
      if (sessionStorage.getItem(coachmarkStorageKey) === 'true') return undefined;
    } catch (_err) { /* storage is optional */ }

    const timer = window.setTimeout(() => setCoachmarkVisible(true), 3000);
    return () => window.clearTimeout(timer);
  }, [eventId, open, coachmarkStorageKey]);

  const appendMessages = (items) => {
    messageRevisionRef.current += 1;
    setMessages((prev) => {
      const next = [...prev, ...items];
      if (messagesStorageKey) {
        try { sessionStorage.setItem(messagesStorageKey, JSON.stringify(next.slice(-50))); } catch (_err) { /* storage is optional */ }
      }
      return next;
    });
  };

  const navigate = useNavigate();
  const go = useCallback((path, params) => {
    const href = linkHref(path, params);
    if (!href) return;
    navigate(href);
  }, [navigate]);

  useEffect(() => {
    if (!eventId) {
      setMessages([]);
      return undefined;
    }

    const key = `galago-chat-history-${eventId}`;
    const requestId = historyRequestRef.current + 1;
    historyRequestRef.current = requestId;
    const revisionAtStart = messageRevisionRef.current;
    let cancelled = false;

    try {
      const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (Array.isArray(cached) && cached.length) setMessages(cached);
      else setMessages([]);
    } catch (_err) {
      setMessages([]);
    }

    api.get(`/events/${eventId}/chat`, { params: { limit: 200 } })
      .then((response) => {
        const serverMessages = response.data?.messages || [];
        // A history request can finish after a new turn. Never let that stale
        // response replace messages the user has just received.
        if (cancelled
          || historyRequestRef.current !== requestId
          || messageRevisionRef.current !== revisionAtStart) return;
        setMessages(serverMessages);
        setPendingAction(response.data?.state?.pending_action || null);
        setFollowUp(response.data?.state?.follow_up || null);
        try { sessionStorage.setItem(key, JSON.stringify(serverMessages.slice(-50))); } catch (_err) { /* storage is optional */ }
      })
      .catch(() => {
        // Keep cached history visible if the initial request is temporarily unavailable.
      });

    return () => {
      cancelled = true;
    };
  }, [eventId]);

  useEffect(() => {
    if (!open) return;
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [open, messages, isSending]);

  useEffect(() => {
    const handlePromptChange = (event) => setInstallPromptVisible(Boolean(event.detail?.visible));
    window.addEventListener('galago-install-prompt-change', handlePromptChange);
    return () => window.removeEventListener('galago-install-prompt-change', handlePromptChange);
  }, []);

  if (!eventId) return null;

  const openFromCoachmark = () => {
    dismissCoachmark();
    setChatOpen(true);
  };

  const send = async (body) => {
    if (isSending) return;
    setError('');
    setDeepLinks([]);
    setPendingAction(null);
    setFollowUp(null);
    setIsSending(true);
    try {
      const response = await api.post(`/events/${eventId}/chat`, body);
      const data = response.data || {};
      setDeepLinks(data.deep_links || []);
      appendMessages([
        { role: 'user', content: body.message || '(confirmed staged change)' },
        { role: 'assistant', content: data.reply || '', citations: data.citations || [] },
      ]);
      if (body.confirm_action_id) {
        window.dispatchEvent(new CustomEvent('galago-chat-mutation', { detail: { eventId, response: data } }));
      }
      setPendingAction(data.pending_action || null);
      setFollowUp(data.follow_up || null);
      setInput('');
    } catch (err) {
      if (err.response?.status === 402) {
        setPremiumOpen(true);
      } else if (err.response?.status === 404) {
        setError('You do not have access to this event.');
      } else if (err.response?.status === 503) {
        setError('The support assistant is not available right now. Try again later.');
      } else if (err.response?.status === 429) {
        setError('Too many requests. Please wait a minute and try again.');
      } else {
        setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsSending(false);
    }
  };

  const submit = (event) => {
    event?.preventDefault();
    const text = input.trim();
    if (!text || isSending) return;
    send({ message: text });
  };

  const submitAttachment = async () => {
    if (!ticketFile || isSending) return;
    const normalized = input.toLowerCase();
    const inferredType = /\b(departure|departing|leaving|outbound)\b/.test(normalized)
      ? 'departure'
      : (/\b(arrival|arriving|incoming|inbound)\b/.test(normalized) ? 'arrival' : null);
    if (!inferredType) {
      appendMessages([
        { role: 'user', content: `Attached ticket: ${ticketFile.name}` },
        { role: 'assistant', content: 'Is this your arrival ticket or your departure ticket? Please mention arrival or departure, then tap Upload again.' },
      ]);
      return;
    }
    setError(''); setDeepLinks([]); setPendingAction(null); setFollowUp(null); setIsSending(true);
    try {
      const form = new FormData();
      form.append('file', ticketFile);
      form.append('ticket_type', inferredType);
      if (input.trim()) form.append('message', input.trim());
      const response = await api.post(`/events/${eventId}/chat/attachment`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const data = response.data || {};
      appendMessages([
        { role: 'user', content: input.trim() || `Attached ticket: ${ticketFile.name}` },
        { role: 'assistant', content: data.reply || '', citations: data.citations || [] },
      ]);
      setPendingAction(data.pending_action || null); setInput(''); setTicketFile(null);
      setFollowUp(data.follow_up || null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      if (err.response?.status === 402) setPremiumOpen(true);
      else setError(err.response?.data?.detail || 'Could not read that ticket.');
    } finally { setIsSending(false); }
  };

  const startResize = (event) => {
    event.preventDefault();
    const handle = event.currentTarget;
    handle.setPointerCapture?.(event.pointerId);
    const panel = handle.parentElement;
    const rect = panel.getBoundingClientRect();
    const move = (moveEvent) => {
      const width = Math.max(288, Math.min(window.innerWidth - 16, rect.right - moveEvent.clientX));
      const height = Math.max(384, Math.min(window.innerHeight - 96, rect.bottom - moveEvent.clientY));
      setPanelSize({ width, height });
    };
    const stop = () => {
      handle.releasePointerCapture?.(event.pointerId);
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', stop);
      handle.removeEventListener('pointercancel', stop);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', stop);
    handle.addEventListener('pointercancel', stop);
  };

  return (
    <>
      {open && (
        <button
          type="button"
          className="fixed inset-0 z-[55] bg-black/10 md:hidden"
          onClick={() => setChatOpen(false)}
          aria-label="Close support assistant"
          data-testid="chat-widget-mobile-backdrop"
        />
      )}

      {coachmarkVisible && !open && (
        <div
          className={cn(
            'fixed z-[65] flex max-w-[calc(100vw-2rem)] items-center gap-2 rounded-xl border border-wedding-sage/20 bg-card px-3 py-2 text-sm font-medium text-card-foreground shadow-lg',
            installPromptVisible
              ? 'bottom-[calc(18rem+env(safe-area-inset-bottom))] right-4 md:bottom-36 md:right-6'
              : 'bottom-[calc(10rem+env(safe-area-inset-bottom))] right-4 md:bottom-24 md:right-6'
          )}
          role="status"
          data-testid="chat-widget-coachmark"
        >
          <button
            type="button"
            onClick={openFromCoachmark}
            className="text-left leading-snug hover:text-wedding-sage"
          >
            Need help? Ask GalaGo Assistant.
          </button>
          <button
            type="button"
            onClick={dismissCoachmark}
            className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground"
            aria-label="Dismiss assistant suggestion"
          >
            <X className="h-3.5 w-3.5" weight="bold" />
          </button>
          <span className="absolute -bottom-1.5 right-7 h-3 w-3 rotate-45 border-b border-r border-wedding-sage/20 bg-card" aria-hidden="true" />
        </div>
      )}

      <button
        type="button"
        onClick={() => setChatOpen((value) => !value)}
        className={cn(
          'fixed z-[70] grid h-14 w-14 place-items-center rounded-full bg-wedding-sage text-white shadow-[0_18px_45px_rgba(93,112,97,0.35)] transition hover:-translate-y-0.5 hover:bg-wedding-sage/90',
          installPromptVisible
            ? 'bottom-[calc(13.5rem+env(safe-area-inset-bottom))] right-4 md:bottom-[10rem] md:right-6'
            : 'bottom-[calc(5.5rem+env(safe-area-inset-bottom))] right-4 md:bottom-6 md:right-6'
        )}
        aria-label={open ? 'Close support assistant' : 'Open support assistant'}
        title="Support assistant"
        data-testid="chat-widget-fab"
      >
        {open ? <X className="h-7 w-7 text-white" weight="bold" /> : <ChatCircleText className="h-7 w-7 text-white" weight="fill" />}
      </button>

      {open && (
        <section
          className={cn(
            'fixed z-[60] flex w-[min(24rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border bg-background shadow-2xl',
            installPromptVisible
              ? 'bottom-[calc(18rem+env(safe-area-inset-bottom))] right-4 md:bottom-[16rem] md:right-6'
              : 'bottom-[calc(10.5rem+env(safe-area-inset-bottom))] right-4 md:bottom-24 md:right-6',
            'h-[min(32rem,calc(100vh-11rem))]'
          )}
          style={{
            minWidth: '18rem',
            minHeight: '24rem',
            maxWidth: 'calc(100vw - 1rem)',
            maxHeight: 'calc(100vh - 6rem)',
            ...(panelSize ? { width: panelSize.width, height: panelSize.height } : {}),
          }}
          data-testid="chat-widget-panel"
        >
          <span
            role="presentation"
            onPointerDown={startResize}
            className="absolute left-0 top-0 z-10 h-5 w-5 cursor-nwse-resize touch-none"
            data-testid="chat-widget-resize-handle"
            title="Resize chat window"
          />
          <header className="flex items-center gap-2 border-b border-[#626C65] bg-[#3F4C42] px-4 py-3 text-[#E0E4E1]">
            <Sparkle className="h-5 w-5 text-[#D4AF37]" weight="fill" />
            <div className="min-w-0">
              <h2 className="truncate font-display text-sm font-semibold text-white">GalaGo Assistant</h2>
              <p className="truncate text-[0.6875rem] text-[#B7BDB9]">
                {currentEvent?.name || 'Your event'} · event help and dashboard actions
              </p>
            </div>
          </header>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-3">
            {messages.length === 0 && !isSending && (
              <div className="space-y-2 py-2">
                <p className="text-xs text-muted-foreground">
                  Ask about your event. Available information and actions follow your event access.
                </p>
                {SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    disabled={isSending}
                    onClick={() => send({ message: suggestion })}
                    className="block w-full rounded-full border bg-card px-3 py-1.5 text-left text-xs text-card-foreground transition hover:border-wedding-sage/50 hover:bg-wedding-sage/10 disabled:opacity-50"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
            {messages.map((message, index) => (
              <ChatBubble key={index} role={message.role} content={message.content} citations={message.citations} />
            ))}
            {isSending && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-md border bg-card px-3.5 py-2.5 text-sm text-muted-foreground">
                  Looking into that…
                </div>
              </div>
            )}
          </div>

          {followUp && !pendingAction && (
            <div className="border-t bg-wedding-sage/5 px-3 py-2.5" data-testid="chat-follow-up">
              {followUp.context && <p className="text-[0.6875rem] font-medium text-muted-foreground">Currently helping with: {followUp.context}</p>}
              <p className="mt-0.5 text-xs text-card-foreground">{followUp.question}</p>
              {followUp.choices?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {followUp.choices.map((choice) => (
                    <button
                      key={choice.message}
                      type="button"
                      disabled={isSending}
                      onClick={() => send({ message: choice.message })}
                      className="rounded-full border border-wedding-sage/40 bg-card px-2.5 py-1 text-xs font-medium text-wedding-sage transition hover:bg-wedding-sage/15 disabled:opacity-50"
                      data-testid="chat-follow-up-choice"
                    >
                      {choice.label}
                    </button>
                  ))}
                </div>
              )}
              <button
                type="button"
                disabled={isSending}
                onClick={() => send({ message: 'Cancel this change' })}
                className="mt-2 text-[0.6875rem] text-muted-foreground underline underline-offset-2 hover:text-card-foreground disabled:opacity-50"
              >
                Cancel this change
              </button>
            </div>
          )}

          {pendingAction && (
            <div className="border-t bg-amber-50 px-3 py-2" data-testid="chat-pending-action">
              <p className="flex items-start gap-1.5 text-xs text-amber-900">
                <WarningCircle className="mt-0.5 h-4 w-4 shrink-0" weight="fill" />
                <span className="min-w-0">{pendingAction.summary}</span>
              </p>
              <div className="mt-2 flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  className="bg-wedding-sage text-white hover:bg-wedding-sage/90"
                  disabled={isSending}
                  onClick={() => send({ message: '', confirm_action_id: pendingAction.action_id })}
                >
                  <Check className="mr-1 h-4 w-4" />
                  Confirm
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={isSending}
                  onClick={() => send({ message: '', cancel_action_id: pendingAction.action_id })}
                >
                  <X className="mr-1 h-4 w-4" />
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {deepLinks.length > 0 && (
            <div className="border-t bg-wedding-sage/5 px-3 py-2" data-testid="chat-deep-links">
              <p className="text-[0.6875rem] font-medium text-muted-foreground">Open in app</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {deepLinks.map((link, index) => (
                  <button
                    key={`${link.path}-${index}`}
                    type="button"
                    onClick={() => go(link.path, link.params)}
                    className="inline-flex items-center gap-1 rounded-full border border-wedding-sage/40 bg-card px-2.5 py-1 text-[0.6875rem] font-medium text-wedding-sage transition hover:bg-wedding-sage/15"
                    data-testid="chat-deep-link"
                  >
                    {link.label}
                    <ArrowUpRight className="h-3 w-3" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && <p className="border-t px-3 py-2 text-xs text-red-600">{error}</p>}

          {ticketFile && <div className="border-t px-3 pt-2 text-xs text-muted-foreground">{ticketFile.name} · mention arrival or departure in your message</div>}
          <form onSubmit={submit} className="flex items-end gap-2 border-t bg-background p-3">
            <input ref={fileInputRef} type="file" accept=".pdf,image/jpeg,image/png,image/webp" className="hidden" data-testid="chat-ticket-file" onChange={(event) => setTicketFile(event.target.files?.[0] || null)} />
            <Button type="button" size="icon" variant="outline" className="h-10 w-10 shrink-0" onClick={() => fileInputRef.current?.click()} disabled={isSending} aria-label="Attach ticket">
              <Paperclip className="h-5 w-5" />
            </Button>
            <textarea
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) submit(event);
              }}
              rows={2}
              placeholder="Ask about your event…"
              className="max-h-24 flex-1 resize-none rounded-lg border bg-card px-3 py-2 text-sm text-card-foreground outline-none focus:border-wedding-sage"
              data-testid="chat-widget-input"
            />
            <Button
              type="submit"
              size="icon"
              className="h-10 w-10 shrink-0 bg-wedding-sage text-white hover:bg-wedding-sage/90"
              disabled={isSending || !input.trim()}
              aria-label="Send message"
            >
              <PaperPlaneTilt className="h-5 w-5" />
            </Button>
            {ticketFile && <Button type="button" onClick={submitAttachment} disabled={isSending} className="h-10 shrink-0 bg-wedding-sage text-white">Upload</Button>}
          </form>
        </section>
      )}

      <AIPremiumDialog
        open={premiumOpen}
        onOpenChange={setPremiumOpen}
        featureName="the Support Assistant"
      />
    </>
  );
}
